import React, { useState, useEffect } from 'react';
import './App.css';
import Auth, { AuthProvider, useAuth } from './Auth';

const API = "https://cybercomply-ai.onrender.com/api";

function LogoutModal({ onConfirm, onCancel }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
    }}>
      <div style={{
        background: 'rgba(13,31,45,0.98)', border: '1px solid rgba(0,255,231,0.2)',
        borderRadius: '12px', padding: '32px', width: '340px', textAlign: 'center',
        boxShadow: '0 20px 60px rgba(0,0,0,0.6)'
      }}>
        <div style={{ fontSize: '36px', marginBottom: '12px' }}>👋</div>
        <div style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '8px', fontFamily: 'Rajdhani, sans-serif', letterSpacing: 1 }}>
          SIGN OUT?
        </div>
        <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '28px', lineHeight: 1.6, fontFamily: 'Share Tech Mono, monospace' }}>
          Are you sure you want to sign out of CyberComply AI?
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button onClick={onCancel} style={{
            flex: 1, padding: '11px', border: '1px solid var(--border)', borderRadius: '6px',
            background: 'transparent', color: 'var(--text-secondary)', fontSize: '13px',
            fontFamily: 'Rajdhani, sans-serif', fontWeight: 600, cursor: 'pointer', letterSpacing: 1
          }}>CANCEL</button>
          <button onClick={onConfirm} style={{
            flex: 1, padding: '11px', border: '1px solid rgba(255,56,96,0.5)', borderRadius: '6px',
            background: 'rgba(255,56,96,0.1)', color: 'var(--red)', fontSize: '13px',
            fontFamily: 'Rajdhani, sans-serif', fontWeight: 700, cursor: 'pointer', letterSpacing: 1
          }}>YES, SIGN OUT</button>
        </div>
      </div>
    </div>
  );
}

function MainApp() {
  const { user, logout } = useAuth();
  const [screen, setScreen]             = useState('onboard');
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [notification, setNotification] = useState(null);
  const [company, setCompany]           = useState({
    name: '', employees: '',
    sector: 'Professional Services',
    target: 'Cyber Essentials (Danzell)',
  });
  const [scanStep, setScanStep]         = useState(0);
  const [gapResults, setGapResults]     = useState([]);
  const [summary, setSummary]           = useState(null);
  const [documents, setDocuments]       = useState({});
  const [docLoading, setDocLoading]     = useState({});
  const [scanError, setScanError]       = useState('');
  const [editingDoc, setEditingDoc]     = useState(null);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const scanSteps = [
    'Authenticating Microsoft 365 tenant (OAuth 2.0)...',
    'Reading device inventory and patch levels...',
    'Checking MFA configuration and conditional access...',
    'Assessing firewall and malware protection status...',
    'Mapping controls against Danzell question set (April 2026)...',
    'Generating gap report and compliance documents...',
  ];

  const startScan = async () => {
    if (!company.name || !company.employees) {
      setNotification({ type: 'error', text: 'Please enter your company name and number of employees.' });
      return;
    }
    setScanError('');
    setScreen('scan');
    setScanStep(0);

    for (let i = 0; i <= scanSteps.length; i++) {
      await new Promise((r) => setTimeout(r, 700));
      setScanStep(i);
    }

    try {
      const response = await fetch(`${API}/analyse`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          org: { name: company.name, size: company.employees, sector: company.sector }
        }),
      });
      const data = await response.json();
      if (data.success) {
        setGapResults(data.gaps);
        setSummary(data.summary);
        setScreen('results');
        setNotification({
          type: 'success',
          text: `Scan complete for ${company.name} — ${data.summary.fails} critical gaps found.`
        });
      } else {
        throw new Error(data.error || 'Analysis failed');
      }
    } catch (err) {
      setScanError('Could not connect to backend. Please try again.');
      setScreen('onboard');
      setNotification({ type: 'error', text: 'Backend connection failed. Please try again.' });
    }
  };

  const generateDoc = async (type) => {
    setDocLoading((prev) => ({ ...prev, [type]: true }));
    try {
      const response = await fetch(`${API}/generate-document`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type,
          org: { name: company.name, size: company.employees, sector: company.sector },
          gaps: gapResults
        }),
      });
      const data = await response.json();
      if (data.success) {
        setDocuments((prev) => ({ ...prev, [type]: data.content }));
        setNotification({ type: 'success', text: 'Document generated successfully.' });
      } else {
        throw new Error(data.error || 'Generation failed');
      }
    } catch (err) {
      setNotification({ type: 'error', text: 'Document generation failed. Please try again.' });
      setDocuments((prev) => ({ ...prev, [type]: null }));
    }
    setDocLoading((prev) => ({ ...prev, [type]: false }));
  };

  const downloadDoc = (title, content) => {
    const date = new Date().toISOString().split('T')[0];
    const fullText = [
      title, '='.repeat(title.length),
      `Company: ${company.name}`,
      `Employees: ${company.employees}`,
      `Sector: ${company.sector}`,
      `Date: ${date}`,
      `Prepared by: CyberComply AI — Cyber Essentials Danzell (April 2026)`,
      '', content
    ].join('\n');
    const blob = new Blob([fullText], { type: 'text/plain;charset=utf-8' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `${company.name.replace(/\s+/g, '_')}_${title.replace(/\s+/g, '_')}_${date}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setNotification({ type: 'success', text: `${title} downloaded successfully.` });
  };

  const docTypes = [
    { type: 'patch',    title: 'Patch Management Policy',    control: 'CE-D5' },
    { type: 'access',   title: 'Access Control & MFA Policy', control: 'CE-D3' },
    { type: 'firewall', title: 'Firewall & Boundary Policy',  control: 'CE-D1' },
  ];

  const fails  = gapResults.filter((g) => g.status === 'fail').length;
  const warns  = gapResults.filter((g) => g.status === 'warn').length;
  const passes = gapResults.filter((g) => g.status === 'pass').length;

  const navScreens = ['onboard', 'scan', 'results', 'docs'];
  const navLabels  = { onboard: 'Organisation', scan: 'Scan', results: 'Gap Report', docs: 'Documents' };

  const canNavigateTo = (s) => {
    const idx = navScreens.indexOf(s);
    if (idx <= 0) return true;
    if (idx === 1) return !!company.name && !!company.employees;
    return gapResults.length > 0;
  };

  return (
    <div className="app">
      {showLogoutModal && (
        <LogoutModal
          onConfirm={() => {
            setShowLogoutModal(false);
            setScreen('onboard');
            setCompany({ name: '', employees: '', sector: 'Professional Services', target: 'Cyber Essentials (Danzell)' });
            setGapResults([]);
            setDocuments({});
            setScanStep(0);
            setNotification(null);
            logout();
          }}
          onCancel={() => setShowLogoutModal(false)}
        />
      )}

      {/* ── TOPBAR ── */}
      <div className="topbar">
        <div className="logo">
          <div className="logo-icon">🛡</div>
          Cyber<span>Comply</span> AI
        </div>
        <div className="topbar-right">
          <div className="badge">Cyber Compliance · UK SMEs 2026</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)', fontFamily: 'Share Tech Mono, monospace' }}>
              {user && user.name ? user.name : user && user.email ? user.email.split('@')[0] : ''}
            </span>
            <div className="topbar-user" onClick={() => setShowLogoutModal(true)} title="Sign out" style={{ cursor: 'pointer' }}>
              {user && user.name ? user.name.substring(0, 2).toUpperCase() : user && user.email ? user.email[0].toUpperCase() : 'U'}
            </div>
          </div>
          <button className="logout-btn" onClick={() => setShowLogoutModal(true)}>LOGOUT</button>
        </div>
      </div>

      {/* ── NOTIFICATION ── */}
      {notification && (
        <div style={{
          background: notification.type === 'success' ? 'rgba(0,230,118,0.08)' : 'rgba(255,56,96,0.08)',
          borderBottom: `1px solid ${notification.type === 'success' ? 'rgba(0,230,118,0.25)' : 'rgba(255,56,96,0.25)'}`,
          color: notification.type === 'success' ? 'var(--green)' : 'var(--red)',
          padding: '10px 24px', fontSize: '12px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          fontFamily: 'Share Tech Mono, monospace', letterSpacing: 0.5
        }}>
          <span>{notification.type === 'success' ? '✓' : '⚠'} {notification.text}</span>
          <span onClick={() => setNotification(null)} style={{ cursor: 'pointer', opacity: 0.6, fontSize: '16px', marginLeft: '16px' }}>✕</span>
        </div>
      )}

      {/* ── NAV ── */}
      <div className="nav">
        {navScreens.map((s, i) => (
          <div key={s}
            className={`nav-item ${screen === s ? 'active' : ''}`}
            onClick={() => canNavigateTo(s) && setScreen(s)}
            style={{ cursor: canNavigateTo(s) ? 'pointer' : 'default' }}>
            <span className="nav-num">{i + 1}</span>
            {navLabels[s]}
          </div>
        ))}
      </div>

      <div className="main">

        {/* ── SCREEN 1: Organisation ── */}
        {screen === 'onboard' && (
          <div className="card">
            <div className="card-header">
              <div className="avatar">
                {company.name ? company.name.substring(0, 2).toUpperCase() : 'CC'}
              </div>
              <div>
                <div className="card-title">Organisation Setup</div>
                <div className="card-sub">// Step 1 of 4 — Enter your organisation details</div>
              </div>
            </div>
            {scanError && (
              <div style={{ background: 'rgba(255,56,96,0.08)', border: '1px solid rgba(255,56,96,0.3)', borderRadius: 4, padding: '12px 16px', fontFamily: 'Share Tech Mono, monospace', fontSize: 12, color: 'var(--red)', marginBottom: 20 }}>
                ⚠ {scanError}
              </div>
            )}
            <div className="fields">
              <div className="field">
                <label>Company Name</label>
                <input type="text" placeholder="e.g. Leicester Tech Solutions Ltd"
                  value={company.name}
                  onChange={(e) => setCompany({ ...company, name: e.target.value })} />
              </div>
              <div className="field">
                <label>Number of Employees</label>
                <input type="number" placeholder="e.g. 18"
                  value={company.employees}
                  onChange={(e) => setCompany({ ...company, employees: e.target.value })} />
              </div>
              <div className="field">
                <label>Industry Sector</label>
                <select value={company.sector} onChange={(e) => setCompany({ ...company, sector: e.target.value })}>
                  <option>Professional Services</option>
                  <option>Technology</option>
                  <option>Accountancy</option>
                  <option>Legal</option>
                  <option>Recruitment</option>
                  <option>Manufacturing</option>
                  <option>Healthcare</option>
                  <option>Finance</option>
                </select>
              </div>
              <div className="field">
                <label>Certification Target</label>
                <select value={company.target} onChange={(e) => setCompany({ ...company, target: e.target.value })}>
                  <option>Cyber Essentials (Danzell)</option>
                  <option>Cyber Essentials Plus</option>
                  <option>ISO 27001</option>
                </select>
              </div>
            </div>
            <button className="btn-primary" onClick={startScan}>
              CONNECT &amp; RUN GAP ANALYSIS →
            </button>
          </div>
        )}

        {/* ── SCREEN 2: Scan ── */}
        {screen === 'scan' && (
          <div className="card scan-card">
            <span className="scan-icon">⚙</span>
            <div className="card-title">Scanning {company.name || 'Organisation'}</div>
            <div className="card-sub" style={{ marginTop: 8 }}>// Secure read-only connection via Microsoft Graph API</div>
            <div className="progress-wrap">
              <div className="progress-bar" style={{ width: `${Math.min((scanStep / scanSteps.length) * 100, 100)}%` }} />
            </div>
            <div className="scan-steps">
              {scanSteps.map((step, i) => (
                <div key={i} className={`scan-step ${i < scanStep ? 'done' : i === scanStep ? 'active' : ''}`}>
                  <div className="step-dot" />
                  {i < scanStep ? '✓ ' : ''}{step}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── SCREEN 3: Gap Report ── */}
        {screen === 'results' && gapResults.length > 0 && (
          <div>
            <div className="alert-box">
              Gap analysis complete for {company.name} — {gapResults.length} controls assessed against Danzell question set (April 2026)
            </div>
            <div className="results-header">
              <div className="card-title">Gap Analysis Report</div>
              <div className="chips">
                <span className="chip chip-red">{fails} critical</span>
                <span className="chip chip-amber">{warns} warnings</span>
                <span className="chip chip-green">{passes} passing</span>
              </div>
            </div>
            <div className="summary-grid">
              <div className="summary-card">
                <div className="summary-num red">{fails}</div>
                <div className="summary-lbl">Critical Gaps</div>
              </div>
              <div className="summary-card">
                <div className="summary-num amber">{warns}</div>
                <div className="summary-lbl">Warnings</div>
              </div>
              <div className="summary-card">
                <div className="summary-num green">{passes}</div>
                <div className="summary-lbl">Controls Passing</div>
              </div>
            </div>
            <div className="section-label">Danzell control gaps — ranked by severity</div>
            <div className="gap-list">
              {gapResults.map((gap) => (
                <div key={gap.id} className={`gap-item ${gap.status}`}>
                  <div className={`gap-dot ${gap.status}`} />
                  <div className="gap-content">
                    <div className="gap-control">{gap.id}</div>
                    <div className="gap-title">{gap.title}</div>
                    <div className="gap-desc">{gap.desc}</div>
                    {gap.fix && <div className="gap-fix">→ Fix: {gap.fix}</div>}
                    {gap.evidence && (
                      <div style={{ fontFamily: 'Share Tech Mono, monospace', fontSize: 10, color: 'var(--text-muted)', marginTop: 5, letterSpacing: 0.5 }}>
                        Evidence: {gap.evidence}
                      </div>
                    )}
                  </div>
                  <div className={`gap-tag tag-${gap.status}`}>
                    {gap.status === 'fail' ? 'FAIL' : gap.status === 'warn' ? 'WARN' : 'PASS'}
                  </div>
                </div>
              ))}
            </div>
            <button className="btn-primary" onClick={() => setScreen('docs')}>
              GENERATE COMPLIANCE DOCUMENTS →
            </button>
          </div>
        )}

        {/* ── SCREEN 4: Documents ── */}
        {screen === 'docs' && (
          <div>
            {editingDoc && (
              <div style={{
                position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                zIndex: 1000, padding: 20
              }}>
                <div style={{
                  background: 'rgba(13,31,45,0.99)', border: '1px solid rgba(0,255,231,0.2)',
                  borderRadius: 10, padding: 28, width: '100%', maxWidth: 700,
                  display: 'flex', flexDirection: 'column', gap: 16, maxHeight: '90vh'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ fontFamily: 'Rajdhani, sans-serif', fontWeight: 700, fontSize: 16, color: 'var(--text-primary)', letterSpacing: 1 }}>
                      EDIT DOCUMENT
                    </div>
                    <span onClick={() => setEditingDoc(null)} style={{ cursor: 'pointer', color: 'var(--text-muted)', fontSize: 20 }}>✕</span>
                  </div>
                  <textarea
                    value={editingDoc.content}
                    onChange={(e) => setEditingDoc({ ...editingDoc, content: e.target.value })}
                    style={{
                      flex: 1, minHeight: 360, background: 'rgba(5,13,20,0.9)',
                      border: '1px solid var(--border)', borderRadius: 6, padding: 16,
                      fontFamily: 'Share Tech Mono, monospace', fontSize: 12,
                      color: 'var(--text-secondary)', lineHeight: 1.9, resize: 'vertical', outline: 'none'
                    }}
                  />
                  <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                    <button className="btn-secondary" onClick={() => setEditingDoc(null)}>CANCEL</button>
                    <button className="btn-primary" style={{ width: 'auto', marginTop: 0, padding: '10px 24px' }}
                      onClick={() => {
                        setDocuments((prev) => ({ ...prev, [editingDoc.type]: editingDoc.content }));
                        setEditingDoc(null);
                        setNotification({ type: 'success', text: 'Document saved successfully.' });
                      }}>
                      SAVE CHANGES
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="section-label">AI-generated compliance documents — Danzell aligned</div>

            {docTypes.map((d) => (
              <div key={d.type} className="card doc-card">
                <div className="doc-title">{d.title}</div>
                <div className="doc-meta">{d.control} · AI Generated · Cyber Essentials Danzell · April 2026</div>

                {!documents[d.type] && !docLoading[d.type] && (
                  <button className="btn-primary" style={{ marginTop: 0 }} onClick={() => generateDoc(d.type)}>
                    GENERATE WITH AI →
                  </button>
                )}

                {docLoading[d.type] && (
                  <div style={{ fontFamily: 'Share Tech Mono, monospace', fontSize: 12, color: 'var(--accent)', padding: '20px 0', letterSpacing: 1, textAlign: 'center' }}>
                    ⚙ AI is generating your document…
                  </div>
                )}

                {documents[d.type] && (
                  <>
                    <pre className="doc-preview">{documents[d.type]}</pre>
                    <div className="doc-actions">
                      <button className="btn-secondary" onClick={() => downloadDoc(d.title, documents[d.type])}>
                        ⬇ Download
                      </button>
                      <button className="btn-secondary" onClick={() => setEditingDoc({ type: d.type, content: documents[d.type] })}>
                        ✎ Edit Draft
                      </button>
                      <button className="btn-secondary" onClick={() => {
                        setDocuments((prev) => ({ ...prev, [d.type]: null }));
                        generateDoc(d.type);
                      }}>
                        ↺ Regenerate
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}

      </div>
    </div>
  );
}

function AppRouter() {
  const { user, authKey } = useAuth();
  if (!user) return <Auth key={authKey} />;
  return <MainApp />;
}

function App() {
  return (
    <AuthProvider>
      <AppRouter />
    </AuthProvider>
  );
}

export default App;